import * as React from 'react';
import { compose } from 'redux';

import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AxiosResponse, AxiosError } from 'axios';

import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import Grid from '@material-ui/core/Grid';

import { ICustomer, ICustomerPagingResponse, ILicense } from '../../@types';
import axios from '../../common/axios-instance/axios-instance';
import ResponsiveTableHead, {
  IColumnData
} from '../../common/responsive-table/responsive-table-head';
import TableToolbar from './table-toolbar';
import { displayDate } from '../../common/utils';

interface ITableState {
  page: number;
  rowsPerPage: number;
  totalElements: number;
  order: 'asc' | 'desc';
  orderBy: string;
  filterText: string;
  rows: ICustomer[];
}

const headers: IColumnData[] = [
  { id: 'companyName', label: 'Company Name' },
  { id: 'pocFirstName', label: 'Point of Contact' }, // need to use poc first name to sort
  { id: 'licenses', label: 'Licenses', sortable: false },
  { id: 'updatedDate', label: 'Last Updated' },
  { id: 'expirationDate', label: 'Earliest Expiration Date', sortable: false } // TODO fix sorting bug on backend and allow sorting
];

const styles = createStyles({
  table: {
    overflowX: 'auto',
    overflowY: 'hidden'
  },
  rowHover: {
    cursor: 'pointer'
  },
  dateTableCell: {
    width: '13%'
  },
  namesTableCell: {
    width: '20%'
  }
});

class CustomerTable extends React.Component<
  RouteComponentProps<any> & WithStyles<typeof styles>,
  ITableState
> {
  readonly state: ITableState = {
    page: 0,
    rowsPerPage: 10,
    totalElements: 0,
    order: 'desc',
    orderBy: 'updatedDate',
    filterText: '',
    rows: []
  };

  componentDidMount = () => {
    this.updateTable();
  };

  handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number
  ) => {
    this.setState({ page }, this.updateTable);
  };

  handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState(
      { page: 0, rowsPerPage: Number(event.target.value) },
      this.updateTable
    );
  };

  handleFilterTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const filterText = event.currentTarget.value;
    this.setState({ filterText }, this.updateTable);
  };

  handleRequestSort = (property: string) => {
    const orderBy = property;
    let order: 'asc' | 'desc' = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, orderBy }, this.updateTable);
  };

  /*
   * both return types are necessary
   * calling from setState requires () => void
   * testing this method will require a promise so we need to actually return one
   */
  updateTable = (): void | Promise<any> => {
    const { page, rowsPerPage, order, orderBy, filterText } = this.state;
    const serverPage = page + 1;
    let url;
    url =
      '/api/customer/page?page=' +
      serverPage +
      '&size=' +
      rowsPerPage +
      '&orderBy=' +
      orderBy +
      '&order=' +
      order +
      '&filterText=' +
      filterText;
    return axios
      .get<ICustomerPagingResponse>(url)
      .then((response: AxiosResponse<ICustomerPagingResponse>) => {
        const content = response.data.content;
        this.setState({
          rows: content,
          totalElements: response.data.totalElements
        });
      })
      .catch((error: AxiosError) => {
        console.log(error.message);
      });
  };

  getLicensesString = (licenses?: ILicense[]) => {
    if (licenses && licenses.length > 0) {
      const appNames = licenses.map(
        license => license.appName + ` (` + license.status + `)`
      );
      let ret = appNames.join(', ');
      if (ret.length > 75) {
        ret = ret.substr(0, 75) + '...';
      }
      return ret;
    } else {
      return 'No licenses';
    }
  };

  getEarliestExpirationDate = (licenses?: ILicense[]) => {
    if (licenses && licenses.length > 0) {
      const expirationDates = licenses.map(license => license.expirationDate);
      const earliestExpiration = expirationDates.reduce((date1, date2) =>
        date1 > date2 ? date2 : date1
      );
      return displayDate(new Date(earliestExpiration));
    } else {
      return 'N/A';
    }
  };

  render(): JSX.Element {
    // TODO if role is not admin, do not render
    const {
      filterText,
      order,
      orderBy,
      rows,
      rowsPerPage,
      page,
      totalElements
    } = this.state;

    const { classes } = this.props;

    const emptyRows = rowsPerPage - rows.length;

    return (
      <div>
        <TableToolbar
          filterText={filterText}
          onFilterTextChange={this.handleFilterTextChange}
        />
        <Grid item container xs={12} className={classes.table}>
          <Table>
            <ResponsiveTableHead
              order={order}
              orderBy={orderBy}
              columnData={headers}
              onRequestSort={this.handleRequestSort}
            />
            <TableBody>
              {rows.map((customer: ICustomer) => {
                return (
                  <TableRow
                    hover
                    key={customer.id}
                    className={classes.rowHover}
                    // tslint:disable-next-line jsx-no-lambda
                    onClick={() =>
                      this.props.history.push('/customers/view/' + customer.id)
                    }
                  >
                    <TableCell className={classes.namesTableCell}>
                      {customer.companyName}
                    </TableCell>
                    <TableCell className={classes.namesTableCell}>
                      {customer.pocFirstName + ' ' + customer.pocLastName}
                    </TableCell>
                    <TableCell>
                      {this.getLicensesString(customer.licenseList)}
                    </TableCell>
                    <TableCell className={classes.dateTableCell}>
                      {displayDate(new Date(customer.updatedDate))}
                    </TableCell>
                    <TableCell className={classes.dateTableCell}>
                      {this.getEarliestExpirationDate(customer.licenseList)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 48 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
        <TablePagination
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          rowsPerPageOptions={[5, 10, 15, 25]}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
        />
      </div>
    );
  }
}

const enhance = compose(
  withStyles(styles),
  withRouter
);

export default enhance(CustomerTable);



// WEBPACK FOOTER //
// ./src/features/customer-table/customer-table.tsx